import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ITEMS_PER_PAGE = 12;

/* ── Animações ── */
const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.04 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 260, damping: 24 },
  },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.15 } },
};

function Temas({ video, onUpdate, setVisivel }) {
  const [temas, setTemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aplicando, setAplicando] = useState(null);

  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState("todos");
  const [page, setPage] = useState(1);

  const scrollRef = useRef(null);

  // ── Buscar temas ──
  useEffect(() => {
    setLoading(true);
    fetch("https://videooferta.com/api/listar_templates.php")
      .then((res) => res.json())
      .then((data) => setTemas(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Erro ao buscar temas:", err))
      .finally(() => setLoading(false));
  }, []);

  // ── Categorias ──
  const categorias = useMemo(() => {
    const cats = temas
      .map((t) => t.categoria)
      .filter((c) => typeof c === "string" && c.trim() !== "");
    return ["todos", ...new Set(cats)];
  }, [temas]);

  // ── Filtro + busca ──
  const temasFiltrados = useMemo(() => {
    const buscaLower = busca.toLowerCase();
    return temas.filter((tema) => {
      const nome = (tema.titulo || "").toLowerCase();
      const categoriaTema = tema.categoria || "";
      return (
        (categoria === "todos" || categoriaTema === categoria) &&
        nome.includes(buscaLower)
      );
    });
  }, [temas, busca, categoria]);

  // ── Paginação ──
  const temasVisiveis = useMemo(
    () => temasFiltrados.slice(0, page * ITEMS_PER_PAGE),
    [temasFiltrados, page]
  );
  const temMais = page * ITEMS_PER_PAGE < temasFiltrados.length;

  // ── Aplicar tema ──
  const handleClickTema = (temaId, musicaId, temaTitulo) => {
    setAplicando(temaId);
    const formData = new FormData();
    formData.append("video_id", video.video_id);
    formData.append("tema_id", temaId);
    formData.append("musica_id", musicaId);
    formData.append("titulo", temaTitulo);

    fetch("https://videooferta.com/api/atualizar_tema_video.php", {
      method: "POST",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          onUpdate();
          setVisivel('produtos');
        }
      })
      .finally(() => setAplicando(null));
  };

  // ── Contagem de resultados ──
  const totalFiltrados = temasFiltrados.length;

  return (
    <div className="col2 temas-panel">
      {/* ─── HEADER ─── */}
      <motion.div
        className="temas-header"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        <div className="temas-header__top">
          <div className="temas-header__title-group">
            <div className="temas-header__icon-wrap">
              <i className="fa-solid fa-swatchbook" />
            </div>
            <div>
              <h2 className="temas-header__title">Temas</h2>
              <p className="temas-header__subtitle">
                {loading
                  ? "Carregando..."
                  : `${totalFiltrados} tema${totalFiltrados !== 1 ? "s" : ""} disponíve${totalFiltrados !== 1 ? "is" : "l"}`}
              </p>
            </div>
          </div>
          <button
            className="temas-header__close"
            onClick={() => setVisivel("")}
            aria-label="Fechar"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* ─── BUSCA ─── */}
        <div className="temas-search">
          <i className="fa-solid fa-magnifying-glass temas-search__icon" />
          <input
            type="text"
            className="temas-search__input"
            placeholder="Buscar tema..."
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setPage(1);
            }}
          />
          {busca && (
            <button
              className="temas-search__clear"
              onClick={() => {
                setBusca("");
                setPage(1);
              }}
              aria-label="Limpar busca"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>

        {/* ─── CATEGORIAS (CHIPS) ─── */}
        <div className="temas-chips" ref={scrollRef}>
          {categorias.map((cat) => (
            <button
              key={cat}
              className={`temas-chip${categoria === cat ? " temas-chip--active" : ""}`}
              onClick={() => {
                setCategoria(cat);
                setPage(1);
              }}
            >
              {cat === "todos" ? "Todos" : cat}
            </button>
          ))}
        </div>
      </motion.div>

      {/* ─── GRID DE TEMAS ─── */}
      <div className="temas-body">
        {loading ? (
          <div className="temas-loading">
            <div className="temas-loading__grid">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="temas-skeleton" />
              ))}
            </div>
          </div>
        ) : temasVisiveis.length === 0 ? (
          <motion.div
            className="temas-empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <i className="fa-solid fa-ghost" />
            <p>Nenhum tema encontrado</p>
            <button
              className="temas-empty__reset"
              onClick={() => {
                setBusca("");
                setCategoria("todos");
                setPage(1);
              }}
            >
              Limpar filtros
            </button>
          </motion.div>
        ) : (
          <>
            <motion.div
              className="temas-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              key={`${categoria}-${busca}-${page}`}
            >
              <AnimatePresence mode="popLayout">
                {temasVisiveis.map((tema) => {
                  const isActive =
                    video?.selo === tema.selo &&
                    video?.background === tema.background;
                  const isApplying = aplicando === tema.id;

                  return (
                    <motion.div
                      key={tema.id}
                      className={`temas-card${isActive ? " temas-card--active" : ""}`}
                      variants={cardVariants}
                      layout
                      whileHover={{ y: -4, transition: { duration: 0.2 } }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() =>
                        !isApplying &&
                        handleClickTema(tema.id, tema.musica_id, tema.titulo)
                      }
                    >
                      <div className="temas-card__img-wrap">
                        <img
                          src={tema.background || tema.thumb}
                          alt={tema.titulo || "Tema"}
                          className="temas-card__img"
                          loading="lazy"
                        />
                        {tema.selo && (
                          <img
                            src={tema.selo}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="temas-card__selo"
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              maxWidth: "70%",
                              maxHeight: "70%",
                              pointerEvents: "none",
                              zIndex: 2,
                            }}
                          />
                        )}
                        <div className="temas-card__overlay">
                          {isApplying ? (
                            <span className="temas-card__spinner" />
                          ) : (
                            <span className="temas-card__apply">
                              <i className="fa-solid fa-check" /> Aplicar
                            </span>
                          )}
                        </div>
                        {isActive && (
                          <span className="temas-card__badge">
                            <i className="fa-solid fa-check" />
                          </span>
                        )}
                      </div>
                      {tema.titulo && (
                        <p className="temas-card__name">{tema.titulo}</p>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>

            {/* Carregar mais */}
            {temMais && (
              <motion.div
                className="temas-loadmore"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <button
                  className="temas-loadmore__btn"
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Carregar mais
                  <i className="fa-solid fa-chevron-down" />
                </button>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Temas;
